import {useGetHealthDatesQuery} from 'presentation/modules/booking/shared/queries/useGetHealthDatesQuery';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useHideSearchBarStore} from 'presentation/modules/booking/shared/store/useHideSearchBarStore';
import {isUnprocessableError} from 'presentation/utils/isUnprocessableError';
import {useCallback, useEffect, useState} from 'react';
import {HandleHourSelectionParams, UseHealthProfessionalSelectionProps} from './types';
import {useQueryParams} from 'presentation/pages';
import {useHealthBooking} from '../../../../hooks/useHealthBooking';
import {useHealthSearchBar} from 'presentation/modules/booking/health/ui/components/HealthSearchBar/hooks/useHealthSearchBar';
import {useProviderNamesStore} from 'presentation/modules/booking/shared/store/useProviderNamesStore';
import getFilteredOptions from 'presentation/modules/booking/shared/utils/getFilteredOptions';
import {useCurrentValueInputTypingStore} from 'presentation/components/Filter/store/useCurrentValueInputTypingStore';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {useQueryClient} from '@tanstack/react-query';
import {TelehealthBookingEnum} from 'presentation/constants';
import {useNavigate} from 'react-router-dom';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useGetTelehealthMostRecentDate} from 'presentation/pages/private/booking/queries/useGetTelehealthMostRecentDate';
import {EntryPointCodeEnum} from 'presentation/constants/entryPointCodeEnum';
import {differenceInDays, isValid, parse} from 'date-fns';
import {TELEMEDICINE_INFO} from 'presentation/modules/booking/shared/constants/telemedicineInfo';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {TypeLocalNameEnum} from 'domain/usecases';
import {getTelehealthStepPosition} from 'presentation/modules/booking/telehealth/utils/getTelehealthStepPosition';

export function useHealthProfessionalSelection({
	handleNextStep,
}: UseHealthProfessionalSelectionProps) {
	const navigate = useNavigate();
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const getProviderNamesState = useProviderNamesStore(state => state.providersName);
	const setHideSearchBarState = useHideSearchBarStore(state => state.setHideSearchBar);
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const currentValueInputTypingState = useCurrentValueInputTypingStore(
		state => state.currentValueInputTyping,
	);
	const [openSearchBestOptionModal, setOpenSearchBestOptionModal] = useState(false);
	const {
		SEARCH_PARAMS: {calendarActive},
		clearSearch,
	} = useQueryParams();
	const queryClient = useQueryClient();
	const {dateAt, specialtyCode, subSpecialtyCode, hasTelehealth} = bookingData;

	const isCalendarActive = !!(calendarActive && calendarActive === 'true');
	const shouldUseUniqueDate = Boolean(isCalendarActive && dateAt);

	const showSelectedDateProfessionalGrid = shouldUseUniqueDate;

	const {activeBookingFlowStep} = useHealthBooking();
	const {handleClearShiftAndSearch} = useHealthSearchBar({activeStep: activeBookingFlowStep});

	const {
		data: availableDates,
		isFetching: isFetchingAvailableDates,
		error: errorAvailableDates,
		refetch: availableDatesRefetch,
	} = useGetHealthDatesQuery({enabled: true, isTelemedicine: false});

	const {
		mutateAsync: mutateTelehealthMostRecentDate,
		isPending: isLoadingTelehealthMostRecentDate,
	} = useGetTelehealthMostRecentDate({
		specialty_code: specialtyCode,
		entryPointCode: Number(EntryPointCodeEnum.V2),
		subspecialty_code: subSpecialtyCode ?? '',
	});

	const hasNoAvailableDates =
		!isFetchingAvailableDates && (!availableDates || availableDates.length === 0);

	const notFoundAvailableDatesError = isUnprocessableError(errorAvailableDates);

	const isLoading = isFetchingAvailableDates;
	const isError = errorAvailableDates && !notFoundAvailableDatesError;

	const showNotFoundState = hasNoAvailableDates || notFoundAvailableDatesError;
	const shouldHideSearchBar = !!errorAvailableDates || hasNoAvailableDates;

	const refetch = useCallback(() => {
		return availableDatesRefetch();
	}, [availableDatesRefetch]);

	const providersNameOptions =
		getProviderNamesState?.medicos?.map(({cdPrestadorFisico, nmMedico}) => ({
			id: cdPrestadorFisico,
			label: nmMedico,
		})) || [];

	const filteredProvidersNameOptions = getFilteredOptions(
		providersNameOptions,
		currentValueInputTypingState,
	);

	const handleSelectHour = async ({
		profCode,
		profName,
		date,
		hour,
	}: HandleHourSelectionParams) => {
		setBookingData(prev => ({
			...prev,
			professionalCode: profCode,
			professionalName: profName,
			dateAt: date,
			hourAt: hour,
		}));

		if (hasTelehealth) {
			try {
				const telehealthMostRecentDate = await mutateTelehealthMostRecentDate();

				if (!telehealthMostRecentDate) {
					handleNextStep();
					return;
				}

				const parsedDate = parse(date, 'dd/MM/yyyy', new Date());
				const parsedMostRecentTelehealthDate = parse(
					telehealthMostRecentDate,
					'dd/MM/yyyy',
					new Date(),
				);

				if (!isValid(parsedDate) || !isValid(parsedMostRecentTelehealthDate)) {
					handleNextStep();
					return;
				}

				const difference = differenceInDays(parsedDate, parsedMostRecentTelehealthDate);
				const minimalDifferenceBetweenHealthAndTelehealth = 5;

				if (difference >= minimalDifferenceBetweenHealthAndTelehealth) {
					handleOpenSearchBestOptionModal();
					return;
				}
			} catch {
				handleNextStep();
				return;
			}
		}

		handleNextStep();
	};

	const goToTelehealthProfessionalSelection = () => {
		handleCloseSearchBestOptionModal();

		setBookingData(prev => ({
			...prev,
			unitCode: TELEMEDICINE_INFO.code,
			unitName: TELEMEDICINE_INFO.name,
			unitAddress: '',
			ufCode: TELEMEDICINE_INFO.ufCode,
			ufName: TELEMEDICINE_INFO.ufName,
			cityName: TELEMEDICINE_INFO.cityName,
			bookingRedirect: true,
			bookingType: BookingTypeEnum.TELEHEALTH,
			hourAt: '',
			dateAt: '',
			professionalCode: '',
			professionalName: '',
			typeLocalName: TypeLocalNameEnum.CIDADE,
		}));

		const {PROFESSIONAL_STEP} = getTelehealthStepPosition();

		setActiveBookingFlowStep(PROFESSIONAL_STEP);
		navigate(TelehealthBookingEnum.TELEHEALTH_BOOKING_FLOW);
		clearSearch();
	};

	const goToNextStep = () => {
		handleCloseSearchBestOptionModal();
		handleNextStep();
	};

	const handleOpenSearchBestOptionModal = () => {
		setOpenSearchBestOptionModal(true);
	};

	const handleCloseSearchBestOptionModal = () => {
		setOpenSearchBestOptionModal(false);
	};

	const resetAvailableTimesAndDates = () => {
		queryClient.resetQueries({
			queryKey: [QUERY_KEYS.HEALTH_AVAILABLE_TIMES],
		});
		queryClient.resetQueries({
			queryKey: [QUERY_KEYS.HEALTH_DATES],
		});
	};

	useEffect(() => {
		setHideSearchBarState(shouldHideSearchBar);
	}, [shouldHideSearchBar, setHideSearchBarState]);

	useEffect(() => {
		resetAvailableTimesAndDates();
	}, []);

	return {
		refetch,
		isLoading,
		isError,
		showNotFoundState,
		handleSelectHour,
		showSelectedDateProfessionalGrid,
		handleClearShiftAndSearch,
		availableDates,
		filteredProvidersNameOptions,
		openSearchBestOptionModal,
		goToTelehealthProfessionalSelection,
		goToNextStep,
		isLoadingTelehealthMostRecentDate,
	};
}
